import { useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { sum } from 'lodash';
import {
  Badge,
  Button,
  Dialog,
  Flex,
  Message,
  Modal,
  Spacer,
  Table,
  toast,
  withErrorBoundary,
} from '@oliasoft-open-source/react-ui-library';
import { FeatureFlags } from '@oliasoft/feature-flags';
import { round } from '@oliasoft-open-source/units';
import translations from 'src/internationalisation/translation-map.json';
import { CellWithExpandingIcon } from 'src/views/time-tracker/cell-with-expanding-icon';
import { FaRegFolderOpen } from 'react-icons/fa';
import { FiFolder } from 'react-icons/fi';
import { IActiveCampaign, ICampaignEstimate } from 'src/common/interfaces/campaigns.interfaces';
import {
  campaignTimeTrackerGenerationConflictClosed,
  generateCampaignTimeTracker,
  getCampaignTimeTracker,
  reorderCampaignEstimates,
  reorderCampaignOperationSequence,
} from 'src/store/entities/campaigns/campaigns';
import { probabilities } from 'src/enums/simulations';
import {
  getCampaignTimeTrackerOperationRows,
  isTimeTrackerSequenceChanged,
} from 'src/views/campaigns/utils/time-tracker-sequence';
import {
  buildSectionRows,
  getDuration,
  getOperationDuration,
  getReorderedSectionRows,
  isEstimateSectionOrderValid,
  toSequencePayload,
} from './operations-sequence-utils';
import { colorFromUuid } from '../utils/utils';
import { FeatureFlagName } from 'src/enums/feature-flags';
import { HOURS_PER_DAY } from 'src/enums/general';
import { CampaignStatus } from 'src/enums/campaigns';

import type { TRootState } from 'src/store/store-types';

type TOperationsSequence = PropsFromRedux & {
  activeCampaign?: IActiveCampaign;
  estimates: ICampaignEstimate[];
};

const cellStyle = {
  style: {
    backgroundColor: 'var(--color-background)',
  },
};

const OperationsSequence = ({
  activeCampaign,
  estimates,
  isOperationSequenceDirty,
  isFetchingCampaignTimeTracker,
  isGeneratingTimeTracker,
  timeTrackerGenerationConflict,
  timeTracker,
  timeTrackerCampaignId,
  getCampaignTimeTracker,
  reorderCampaignOperationSequence,
  reorderCampaignEstimates,
  generateCampaignTimeTracker,
  campaignTimeTrackerGenerationConflictClosed,
}: TOperationsSequence) => {
  const { t } = useTranslation();

  const [expandedSectionIds, setExpandedSectionIds] = useState<string[]>([]);
  const [expandedEstimateIds, setExpandedEstimateIds] = useState<string[]>([]);

  const toggleExpandEstimate = (estimateId: string) => {
    return expandedEstimateIds.includes(estimateId)
      ? setExpandedEstimateIds(expandedEstimateIds.filter((id) => id !== estimateId))
      : setExpandedEstimateIds([...expandedEstimateIds, estimateId]);
  };

  const campaignId = activeCampaign?.campaignId;
  const isCampaignTimeTrackerActive = FeatureFlags.isActive(FeatureFlagName.CampaignTimeTracker);

  useEffect(() => {
    if (isCampaignTimeTrackerActive) {
      setExpandedEstimateIds(estimates.map((estimate) => estimate.campaignsEstimatesId));
    }
    if (campaignId && isCampaignTimeTrackerActive && activeCampaign?.status === CampaignStatus.Actual) {
      getCampaignTimeTracker(campaignId);
    }
  }, [campaignId, isCampaignTimeTrackerActive]);

  const sectionRows = useMemo(
    () => buildSectionRows(estimates, activeCampaign?.operationSequence),
    [activeCampaign?.operationSequence, estimates],
  );
  const operationRows = useMemo(
    () => getCampaignTimeTrackerOperationRows(timeTrackerCampaignId === campaignId ? timeTracker : [], campaignId),
    [campaignId, timeTracker, timeTrackerCampaignId],
  );
  const hasLoadedTimeTracker = !!campaignId && timeTrackerCampaignId === campaignId && !isFetchingCampaignTimeTracker;
  const hasTimeTracker = operationRows.length > 0;
  const isTimeTrackerOutOfSync = hasTimeTracker && isTimeTrackerSequenceChanged(activeCampaign, operationRows);
  const showGenerateTimeTrackerButton =
    isCampaignTimeTrackerActive && !!activeCampaign && hasLoadedTimeTracker && !hasTimeTracker;
  const showRegenerateTimeTrackerButton = isCampaignTimeTrackerActive && hasLoadedTimeTracker && isTimeTrackerOutOfSync;

  const toggleExpandSection = (projectSectionId: string) => {
    return expandedSectionIds.includes(projectSectionId)
      ? setExpandedSectionIds(expandedSectionIds.filter((id) => id !== projectSectionId))
      : setExpandedSectionIds([...expandedSectionIds, projectSectionId]);
  };

  const tableRows: any[] = [];
  sectionRows.forEach((sectionRow, sequenceIndex) => {
    const { estimate, section, sectionIndex, label, projectSectionId, campaignsEstimatesId } = sectionRow;
    const isExpandedSection = expandedSectionIds.includes(projectSectionId);
    const isExpandedEstimate = expandedEstimateIds.includes(estimate.campaignsEstimatesId);
    if (isExpandedEstimate || (!isExpandedEstimate && sectionIndex === 0)) {
      tableRows.push({
        sequenceIndex,
        estimatePosition: estimate.position,
        noDrag: !isCampaignTimeTrackerActive && sectionIndex > 0,
        cells: [
          {
            value: (
              <CellWithExpandingIcon
                cellContent={estimate.estimateName}
                icon={isExpandedEstimate ? <FaRegFolderOpen /> : <FiFolder />}
                isExpanded={!isCampaignTimeTrackerActive}
                index={isCampaignTimeTrackerActive ? 1 : sectionIndex}
                onClick={() => {
                  if (!isCampaignTimeTrackerActive) {
                    toggleExpandEstimate(estimate.campaignsEstimatesId);
                  }
                }}
                isBold
                isBadge
                badgeColor={colorFromUuid(campaignsEstimatesId, 90)}
                badgeTextColor={colorFromUuid(campaignsEstimatesId, 30)}
                textAlign="left"
              />
            ),
            ...cellStyle,
          },
          {
            value: isExpandedEstimate ? (
              <CellWithExpandingIcon
                cellContent={label}
                index={0}
                icon={isExpandedSection ? <FaRegFolderOpen /> : <FiFolder />}
                isExpanded={isExpandedSection}
                isBold
                onClick={() => toggleExpandSection(projectSectionId)}
                textAlign="left"
              />
            ) : (
              <Badge
                color={colorFromUuid(estimate.campaignsEstimatesId, 90)}
                textColor={colorFromUuid(estimate.campaignsEstimatesId, 30)}
                title={`${estimate.simulation.section_durations?.labels?.length} / ${estimate.simulation?.operations?.length}`}
              />
            ),
            ...cellStyle,
          },
          {
            value: isExpandedEstimate
              ? getDuration(estimate, probabilities.MEAN, sectionIndex)
              : round(sum(estimate.simulation.section_durations?.durations[probabilities.MEAN]) / HOURS_PER_DAY, 2),
            ...cellStyle,
          },
          {
            value: isExpandedEstimate
              ? getDuration(estimate, probabilities.P10, sectionIndex)
              : round(sum(estimate.simulation.section_durations?.durations[probabilities.P10]) / HOURS_PER_DAY, 2),
            ...cellStyle,
          },
          {
            value: isExpandedEstimate
              ? getDuration(estimate, probabilities.P50, sectionIndex)
              : round(sum(estimate.simulation.section_durations?.durations[probabilities.P50]) / HOURS_PER_DAY, 2),
            ...cellStyle,
          },
          {
            value: isExpandedEstimate
              ? getDuration(estimate, probabilities.P90, sectionIndex)
              : round(sum(estimate.simulation.section_durations?.durations[probabilities.P90]) / HOURS_PER_DAY, 2),
            ...cellStyle,
          },
        ],
      });
    }

    if (isExpandedSection && isExpandedEstimate) {
      (section.operationIds ?? []).forEach((operationId: string) => {
        const operationIdx = estimate.simulation?.operations?.findIndex((op) => op.id === operationId);
        if (operationIdx === undefined || operationIdx === -1) {
          return;
        }

        tableRows.push({
          noDrag: true,
          cells: [
            { value: '' },
            {
              value: <span style={{ paddingLeft: '20px' }}>{estimate.simulation.operations[operationIdx].name}</span>,
            },
            {
              value: getOperationDuration(estimate, probabilities.MEAN, operationIdx),
            },
            {
              value: getOperationDuration(estimate, probabilities.P10, operationIdx),
            },
            {
              value: getOperationDuration(estimate, probabilities.P50, operationIdx),
            },
            {
              value: getOperationDuration(estimate, probabilities.P90, operationIdx),
            },
          ],
        });
      });
    }
  });

  const table = {
    draggable: true,
    actionsRight: true,
    headers: [
      {
        cells: [
          { value: t(translations.estimate) },
          { value: t(translations.campaigns_sectionOperation) },
          { value: 'Mean' },
          { value: 'P10' },
          { value: 'P50' },
          { value: 'P90' },
        ],
      },
      {
        cells: [
          { value: '' },
          { value: '' },
          { value: t(translations.days) },
          { value: t(translations.days) },
          { value: t(translations.days) },
          { value: t(translations.days) },
        ],
      },
    ],
    rows: tableRows,
  };

  const onEstimatesListReorder = (newList: { from: number; to: number }) => {
    const from = tableRows[newList.from].estimatePosition;
    const to = tableRows[newList.to].estimatePosition;
    reorderCampaignEstimates({
      campaignId: estimates[0].campaignId,
      from,
      to,
    });
  };

  const onListReorder = (newList: { from: number; to: number }) => {
    if (!activeCampaign) {
      return;
    }

    const updatedRows = getReorderedSectionRows(sectionRows, tableRows, newList.from, newList.to);
    if (!updatedRows) {
      return;
    }

    if (!isEstimateSectionOrderValid(updatedRows)) {
      toast({
        message: {
          type: 'Warning',
          content: t(translations.campaigns_cannotChangeEstimateSectionOrder),
        },
      });
      return;
    }

    reorderCampaignOperationSequence({
      campaignId: activeCampaign.campaignId,
      sequence: toSequencePayload(updatedRows),
    });
  };

  const canListReorder = (newList: { from: number; to: number }) => {
    if (!isCampaignTimeTrackerActive) {
      return true;
    }
    const updatedRows = getReorderedSectionRows(sectionRows, tableRows, newList.from, newList.to);

    return !!updatedRows && isEstimateSectionOrderValid(updatedRows);
  };

  const onGenerateTimeTracker = () => {
    if (!activeCampaign || isGeneratingTimeTracker) {
      return;
    }

    generateCampaignTimeTracker({
      campaignId: activeCampaign.campaignId,
      sequence: toSequencePayload(sectionRows),
      force: isOperationSequenceDirty || isTimeTrackerOutOfSync,
    });
  };

  const onConfirmRegenerate = () => {
    const conflict = timeTrackerGenerationConflict;
    if (!conflict) {
      return;
    }

    campaignTimeTrackerGenerationConflictClosed();
    generateCampaignTimeTracker({
      ...conflict,
      force: true,
    });
  };

  const showOverwriteModal = timeTrackerGenerationConflict?.campaignId === activeCampaign?.campaignId;

  return (
    <div>
      <Spacer />
      {showRegenerateTimeTrackerButton && (
        <>
          <Message
            message={{
              content: (
                <Flex justifyContent="space-between" alignItems="center" gap="10px">
                  <span>{t(translations.campaigns_timeTrackerOutOfSyncWithOperationSequence)}</span>
                  <Button
                    label={t(translations.campaigns_generateTimeTrackerOverwriteTitle)}
                    onClick={onGenerateTimeTracker}
                    disabled={sectionRows.length === 0 || isGeneratingTimeTracker}
                    loading={isGeneratingTimeTracker}
                    testId="campaign-operation-sequence-regenerate"
                    small
                    colored
                  />
                </Flex>
              ),
              type: 'Warning',
              visible: true,
              width: '100%',
            }}
          />
          <Spacer />
        </>
      )}
      {showGenerateTimeTrackerButton && (
        <>
          <Flex justifyContent="flex-end">
            <Button
              label={t(translations.campaigns_generateTimeTracker)}
              onClick={onGenerateTimeTracker}
              disabled={!activeCampaign || sectionRows.length === 0 || isGeneratingTimeTracker}
              loading={isGeneratingTimeTracker}
              small
              colored
            />
          </Flex>
          <Spacer />
        </>
      )}
      <Table
        table={table}
        onListReorder={isCampaignTimeTrackerActive ? onListReorder : onEstimatesListReorder}
        canListReorder={canListReorder}
      />
      {showOverwriteModal && (
        <Modal visible centered>
          <Dialog
            dialog={{
              heading: t(translations.campaigns_generateTimeTrackerOverwriteTitle),
              content: <>{t(translations.campaigns_generateTimeTrackerOverwriteWarning)}</>,
              footer: (
                <>
                  <Button
                    label={t(translations.campaigns_generateTimeTracker)}
                    colored
                    loading={isGeneratingTimeTracker}
                    onClick={onConfirmRegenerate}
                  />
                  <Button
                    label={t(translations.cancel)}
                    onClick={() => campaignTimeTrackerGenerationConflictClosed()}
                  />
                </>
              ),
              onClose: campaignTimeTrackerGenerationConflictClosed,
            }}
          />
        </Modal>
      )}
    </div>
  );
};

const mapStateToProps = ({ entities }: TRootState) => {
  const {
    list,
    isFetchingCampaignTimeTracker,
    isGeneratingTimeTracker,
    timeTrackerGenerationConflict,
    operationSequenceDirtyCampaignIds,
    timeTracker,
    timeTrackerCampaignId,
  } = entities.campaigns;
  const activeCampaign = list.find((campaign) => campaign.active);
  return {
    activeCampaign,
    estimates: activeCampaign?.estimates || [],
    isOperationSequenceDirty: activeCampaign
      ? operationSequenceDirtyCampaignIds.includes(activeCampaign.campaignId)
      : false,
    isFetchingCampaignTimeTracker,
    isGeneratingTimeTracker,
    timeTrackerGenerationConflict,
    timeTracker,
    timeTrackerCampaignId,
  };
};

const mapDispatchToProps = {
  getCampaignTimeTracker,
  reorderCampaignEstimates,
  reorderCampaignOperationSequence,
  generateCampaignTimeTracker,
  campaignTimeTrackerGenerationConflictClosed,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = withErrorBoundary(connector(OperationsSequence), {
  style: { position: 'absolute', top: '50px' },
});

export { Container as OperationsSequence };
